// =============================================================================
// BidSitesCard — T-359
// Multi-site / "milk run" tenders (e.g. a 3-site DECON decom): manage ALL the
// sites on a BID, not just the anchor. Backed by bid_sites (migration 217).
//
// Rules kept simple + safe:
//   * bids.site_id remains the PRIMARY site — the whole existing world
//     (promote, map, Move-to-BID, quote inherit) keys off it untouched.
//   * "Make primary" writes BOTH bid_sites.is_primary and bids.site_id, so the
//     two never drift.
//   * Removing the primary isn't allowed while other sites exist (pick a new
//     primary first).
// =============================================================================

import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { MapPin, Plus, Star, Trash2, Search } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Card, Button, Input, Spinner, Pill } from "@/components/ui";

interface BidSiteRow {
  bid_site_id: string;
  site_id: string;
  is_primary: boolean;
  sort_order: number;
  sites: { site_code: string | null; site_name: string | null; suburb: string | null; state: string | null } | null;
}

export default function BidSitesCard({ bidId, canEdit }: { bidId: string; canEdit: boolean }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [q, setQ] = useState("");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["bid_sites", bidId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bid_sites")
        .select("bid_site_id, site_id, is_primary, sort_order, sites:site_id ( site_code, site_name, suburb, state )")
        .eq("bid_id", bidId)
        .order("is_primary", { ascending: false })
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as BidSiteRow[];
    },
  });

  // Site search for the add flow — only when the picker is open + 2+ chars.
  const { data: found = [], isFetching: searching } = useQuery({
    queryKey: ["bid_sites_search", q],
    enabled: adding && q.trim().length >= 2,
    queryFn: async () => {
      const term = q.trim();
      const { data, error } = await supabase
        .from("sites")
        .select("site_id, site_code, site_name, suburb, state")
        .or(`site_name.ilike.%${term}%,site_code.ilike.%${term}%,suburb.ilike.%${term}%`)
        .limit(8);
      if (error) throw error;
      return (data ?? []) as any[];
    },
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["bid_sites", bidId] });
    qc.invalidateQueries({ queryKey: ["bid", bidId] });   // summary card shows the primary
  };

  const addSite = useMutation({
    mutationFn: async (siteId: string) => {
      const makePrimary = rows.length === 0;   // first site becomes the anchor
      const { error } = await supabase.from("bid_sites").insert({
        bid_id: bidId, site_id: siteId, is_primary: makePrimary,
        sort_order: rows.length,
      });
      if (error) throw error;
      if (makePrimary) {
        const { error: e2 } = await supabase.from("bids").update({ site_id: siteId }).eq("bid_id", bidId);
        if (e2) throw e2;
      }
    },
    onSuccess: () => { setQ(""); setAdding(false); invalidate(); },
    onError: (e: any) => window.alert(`Couldn't add site: ${e?.message ?? String(e)}`),
  });

  const makePrimary = useMutation({
    mutationFn: async (r: BidSiteRow) => {
      // Both writes — bid_sites flags + the bids.site_id anchor stay in sync.
      const { error: e1 } = await supabase.from("bid_sites").update({ is_primary: false }).eq("bid_id", bidId);
      if (e1) throw e1;
      const { error: e2 } = await supabase.from("bid_sites").update({ is_primary: true }).eq("bid_site_id", r.bid_site_id);
      if (e2) throw e2;
      const { error: e3 } = await supabase.from("bids").update({ site_id: r.site_id }).eq("bid_id", bidId);
      if (e3) throw e3;
    },
    onSuccess: invalidate,
    onError: (e: any) => window.alert(`Couldn't set primary: ${e?.message ?? String(e)}`),
  });

  const removeSite = useMutation({
    mutationFn: async (r: BidSiteRow) => {
      if (r.is_primary && rows.length > 1) {
        throw new Error("This is the primary site — make another site primary first.");
      }
      const { error } = await supabase.from("bid_sites").delete().eq("bid_site_id", r.bid_site_id);
      if (error) throw error;
      if (r.is_primary) {
        // Last site removed — clear the anchor too.
        const { error: e2 } = await supabase.from("bids").update({ site_id: null }).eq("bid_id", bidId);
        if (e2) throw e2;
      }
    },
    onSuccess: invalidate,
    onError: (e: any) => window.alert(`${e?.message ?? String(e)}`),
  });

  const already = new Set(rows.map(r => r.site_id));

  return (
    <Card>
      <div className="p-5">
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <MapPin size={15} className="text-accent" />
            <span className="text-[13px] font-semibold text-text">Sites on this bid</span>
            {rows.length > 1 && <Pill label={`${rows.length} sites — milk run`} tone="bid" small />}
          </div>
          {canEdit && (
            <Button size="sm" variant="secondary" icon={Plus} onClick={() => setAdding(a => !a)}>
              {adding ? "Close" : "Add site"}
            </Button>
          )}
        </div>
        <p className="text-[11px] text-text-muted mb-3">
          Multi-site tender? Link every site here. The ★ primary is the anchor the
          quote, map and promote key off — the rest ride along for scope + records.
        </p>

        {adding && (
          <div className="mb-3 border border-line rounded-md p-3 bg-ink-raised">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
              <Input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="Search site name / code / suburb…"
                className="pl-8"
                autoFocus
              />
            </div>
            {q.trim().length >= 2 && (
              <div className="mt-2 divide-y divide-line">
                {searching ? (
                  <div className="py-3 flex justify-center"><Spinner size={14} /></div>
                ) : found.length === 0 ? (
                  <div className="py-2 text-[12px] text-text-muted">No sites match — check the site register.</div>
                ) : found.map(s => (
                  <button
                    key={s.site_id}
                    type="button"
                    disabled={already.has(s.site_id) || addSite.isPending}
                    onClick={() => addSite.mutate(s.site_id)}
                    className="w-full text-left py-2 px-1 text-[12px] flex items-center gap-2 hover:bg-ink rounded disabled:opacity-40"
                  >
                    <span className="font-mono text-accent">{s.site_code ?? "—"}</span>
                    <span className="text-text">{s.site_name}</span>
                    <span className="text-text-muted">{[s.suburb, s.state].filter(Boolean).join(", ")}</span>
                    {already.has(s.site_id) && <span className="ml-auto text-[10px] text-text-muted">already linked</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isLoading ? (
          <div className="py-4 flex justify-center"><Spinner size={16} /></div>
        ) : rows.length === 0 ? (
          <div className="text-[12px] text-text-muted py-2">
            No sites linked yet{canEdit ? " — add the first (it becomes the primary)." : "."}
          </div>
        ) : (
          <div className="divide-y divide-line">
            {rows.map(r => (
              <div key={r.bid_site_id} className="py-2 flex items-center gap-2.5">
                <Star
                  size={14}
                  className={r.is_primary ? "text-warn fill-warn flex-shrink-0" : "text-text-muted/40 flex-shrink-0"}
                />
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] text-text">
                    <span className="font-mono text-accent mr-2">{r.sites?.site_code ?? "—"}</span>
                    {r.sites?.site_name ?? "Unknown site"}
                    {r.is_primary && <span className="ml-2 text-[10px] text-warn">primary</span>}
                  </div>
                  <div className="text-[11px] text-text-muted">
                    {[r.sites?.suburb, r.sites?.state].filter(Boolean).join(", ")}
                  </div>
                </div>
                {canEdit && !r.is_primary && (
                  <button
                    type="button"
                    onClick={() => makePrimary.mutate(r)}
                    className="text-[11px] text-text-muted hover:text-warn px-2 py-1 rounded border border-line hover:border-warn/50 transition-colors"
                    title="Make this the primary/anchor site"
                  >
                    Make primary
                  </button>
                )}
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeSite.mutate(r)}
                    className="text-text-muted hover:text-danger p-1.5 rounded transition-colors"
                    title={r.is_primary && rows.length > 1 ? "Make another site primary first" : "Remove site"}
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
